/**
 * @file HomeV2.jsx
 * @description Therapist-first conversion homepage (the acquisition overhaul of
 *   HomePage.jsx). Acts as a traffic router + trust builder + conversion page:
 *   a sub-5-second hero, two co-primary therapist CTAs (Find Cases / Free
 *   Payment Tracker), a Case Finder preview with the open-case carousel, the
 *   provider highlights, then the free tracker showcase, how-it-works, ONE merged pitch section (pain points, "not a
 *   job board", why providers choose us), FAQ, then the lower-priority agency
 *   path just before the discovery hub and footer. All copy is real
 *   crawlable DOM and i18n-keyed (EN + ES). FAQ emits FAQPage JSON-LD.
 *
 *   The live homepage at / (with the Spanish twin at /es).
 *   Reuses TrackerShowcase, PublicFooter, Logo, LanguageSwitcher, Icons, theme.
 * @module components/HomeV2
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { T, typeColor, typeLabel } from "../theme";
import {
  MapPinIcon, ClockIcon, DollarIcon, CalendarIcon, ShieldIcon, ZapIcon,
  CheckIcon, CheckCircleIcon, BriefcaseIcon,
  XIcon, UsersIcon, BellIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import TrackerShowcase from "./TrackerShowcase";
import HomeCaseCard, { HomeCaseCardSkeleton } from "./HomeCaseCard";
import { usePublicCaseBrowsing } from "../lib/publicBrowsing";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A } from "../lib/analytics";
import { getPlans, getStarterPromoDates } from "../lib/plans";
import { getAgencyPricing } from "../lib/agencyPricing";
import { loadAllConfigs } from "../lib/configService";
import { loadPublicOpenCases } from "../lib/caseService";
import { loadPublicProviderHighlights } from "../lib/profileService";
import { sortCasesForVisitor, publicCasePath } from "../lib/homeCases";
import { getVisitorState } from "../lib/visitorGeo";
import { useOnScreen } from "../hooks/useOnScreen";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import LazyVideo from "./LazyVideo";
import { otherLiveStates, LOCATIONS_INDEX_SLUG } from "../lib/seoLocations";

// How it works video: vertical cut for phones, 16:9 for wider screens (files in public/marketing/video).
const HIW_VIDEO = {
  phone: {
    sources: [
      { src: "/marketing/video/how-it-works-vertical-540.webm", type: "video/webm" },
      { src: "/marketing/video/how-it-works-vertical-540.mp4", type: "video/mp4" },
    ],
    poster: "/marketing/video/how-it-works-vertical-poster.webp", width: 540, height: 960,
  },
  wide: {
    sources: [
      { src: "/marketing/video/how-it-works-720.webm", type: "video/webm" },
      { src: "/marketing/video/how-it-works-720.mp4", type: "video/mp4" },
    ],
    poster: "/marketing/video/how-it-works-poster.webp", width: 1280, height: 720,
  },
};

// View id for the free tracker landing (its path is the canonical SEO slug).
const TRACKER_VIEW = "free-aba-timesheet-payment-tracker";

const NAVY      = "#0F1024";
const NAVY_MID  = "#1a1f3c";
const INDIGO    = T.indigo;
const INDIGO_LT = "#818cf8";
const BORDER    = "rgba(226,232,240,0.8)";
const TEXT_MID  = "#475569";
const TEXT_LIGHT= "#94A3B8";

// Homepage discovery hub (Phase 4C) — grouped internal links to the public SEO
// surface, organized by market/audience so the section reads as a directory,
// not a loose link dump. Canonical slugs only (no /home alias); every `to`
// here must resolve via seoHref/onNavigate exactly like SeoLink elsewhere.
const DISCOVERY_GROUPS = [
  {
    key: "florida",
    titleKey: "homeV2.discoveryGroupFloridaTitle",
    links: [
      { to: "rbt-jobs-florida", labelKey: "homeV2.discoveryLinkRbtJobsFlorida" },
      { to: "aba-cases-florida", labelKey: "homeV2.discoveryLinkAbaCasesFlorida" },
      { to: "rbt-jobs-south-florida", labelKey: "homeV2.discoveryLinkRbtJobsSouthFlorida" },
      { to: "aba-staffing-florida", labelKey: "homeV2.discoveryLinkAbaStaffingFlorida" },
    ],
  },
  {
    key: "south-florida",
    titleKey: "homeV2.discoveryGroupSouthFloridaTitle",
    links: [
      { to: "rbt-jobs-miami-fl", labelKey: "homeV2.discoveryLinkRbtJobsMiami" },
      { to: "rbt-jobs-broward-fl", labelKey: "homeV2.discoveryLinkRbtJobsBroward" },
      { to: "rbt-jobs-palm-beach-fl", labelKey: "homeV2.discoveryLinkRbtJobsPalmBeach" },
      { to: "rbt-jobs-fort-lauderdale-fl", labelKey: "homeV2.discoveryLinkRbtJobsFortLauderdale" },
      { to: "rbt-jobs-hialeah-fl", labelKey: "homeV2.discoveryLinkRbtJobsHialeah" },
      { to: "rbt-jobs-doral-fl", labelKey: "homeV2.discoveryLinkRbtJobsDoral" },
      { to: "rbt-jobs-kendall-fl", labelKey: "homeV2.discoveryLinkRbtJobsKendall" },
      { to: "rbt-jobs-west-palm-beach-fl", labelKey: "homeV2.discoveryLinkRbtJobsWestPalmBeach" },
    ],
  },
  {
    key: "north-carolina",
    titleKey: "homeV2.discoveryGroupNorthCarolinaTitle",
    links: [
      { to: "rbt-jobs-charlotte-nc", labelKey: "homeV2.discoveryLinkRbtJobsCharlotte" },
      { to: "rbt-jobs-north-carolina", labelKey: "homeV2.discoveryLinkRbtJobsNorthCarolina" },
      { to: "aba-cases-charlotte-nc", labelKey: "homeV2.discoveryLinkAbaCasesCharlotte" },
      { to: "bcba-jobs-charlotte-nc", labelKey: "homeV2.discoveryLinkBcbaJobsCharlotte" },
      { to: "rbt-staffing-charlotte-nc", labelKey: "homeV2.discoveryLinkRbtStaffingCharlotte" },
    ],
  },
  {
    key: "provider-resources",
    titleKey: "homeV2.discoveryGroupProviderResourcesTitle",
    links: [
      { to: "aba-resources", labelKey: "homeV2.discoveryLinkAbaResources" },
      { to: "aba-provider-waitlist", labelKey: "homeV2.discoveryLinkJoinCaseAlerts" },
      { to: "how-to-choose-an-aba-case", labelKey: "homeV2.discoveryLinkHowToChooseCase" },
      { to: "how-to-become-an-rbt", labelKey: "homeV2.discoveryLinkHowToBecomeRbt" },
      { to: "rbt-resume-guide", labelKey: "homeV2.discoveryLinkRbtResumeGuide" },
      { to: "rbt-interview-questions", labelKey: "homeV2.discoveryLinkRbtInterviewQuestions" },
    ],
  },
  {
    key: "for-agencies",
    titleKey: "homeV2.discoveryGroupForAgenciesTitle",
    links: [
      { to: "aba-agencies", labelKey: "homeV2.discoveryLinkForAgencies" },
      { to: "post-aba-cases", labelKey: "homeV2.discoveryLinkPostAbaCases" },
      { to: "rbt-staffing-miami-fl", labelKey: "homeV2.discoveryLinkRbtStaffingMiami" },
      { to: "aba-staffing-florida", labelKey: "homeV2.discoveryLinkAbaStaffingFloridaAgency" },
    ],
  },
];

// The released states beyond the hand-picked Florida and North Carolina groups (page engine, lib/seoLocations):
// each new state shows up here the week it is released, capped so the homepage never becomes a link list, plus
// the "all states" index. Labels are built here (not i18n keys per link) because the list is data-driven.
const MORE_STATES_MAX = 6;
function moreStatesGroup(t, isEs) {
  const links = otherLiveStates(["FL", "NC"]).slice(0, MORE_STATES_MAX).map((s) => ({
    to: (s.hub || s.places[0]).page.slug,
    label: t("homeV2.discoveryLinkAbaJobsInState", { state: isEs ? s.nameEs : s.name }),
  }));
  links.push({ to: LOCATIONS_INDEX_SLUG, label: t("homeV2.discoveryLinkAllStates") });
  return { key: "more-states", titleKey: "homeV2.discoveryGroupMoreStatesTitle", links };
}

// Therapist FAQ keys (reused from therapistLanding — already EN+ES) → FAQPage schema.
const FAQ_KEYS = ["faq1", "faq2", "faq3", "faq4", "faq5", "faq6", "faq7", "faq8", "faq11", "faq12"];

// ── Provider highlight card — minimal, non-identifying (initial + city/state
//    + credential) sourced from the public_provider_highlights RPC. ──
function ProviderCard({ p }) {
  const roleUpper = String(p.role || "").toUpperCase();
  const tc = typeColor(roleUpper);
  const cityState = [p.city, p.state].filter(Boolean).join(", ");
  return (
    <div style={{
      background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14,
      padding: "20px 16px", boxShadow: "0 2px 14px rgba(15,16,36,0.06)", textAlign: "center",
      height: "100%",
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: "50%", background: tc.accent, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px",
        fontFamily: T.fontMono, fontSize: 16, fontWeight: 800,
      }}>
        {p.initial}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginBottom: 6 }}>{cityState}</div>
      <span style={{
        display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 800,
        color: tc.accent, background: tc.bg, borderRadius: 7, padding: "4px 9px", letterSpacing: "0.02em",
      }}>
        {typeLabel(roleUpper)}
      </span>
    </div>
  );
}

// ── Reusable horizontal carousel (single implementation shared by the open
//    cases and providers strips). Manual prev/next only — no auto-advance. ──
// `autoAdvance` is opt-in and deliberately NOT used for the cases carousel:
// those cards contain the Apply Now CTA, and a card sliding out from under a
// click is a broken conversion. It's on for the providers strip, which is
// passive social proof with nothing clickable inside.
//
// Guardrails (all required, not polish): pauses on hover and on keyboard
// focus, and stops permanently once the visitor takes control — together
// these satisfy WCAG 2.2.2 (auto-moving content needs a pause mechanism).
// It also honors prefers-reduced-motion and idles while scrolled off-screen.
function CardCarousel({ children, prevLabel, nextLabel, itemWidth = 280, autoAdvance = false }) {
  const trackRef = useRef(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  // Visitor grabbed the wheel — never auto-advance again this page view.
  const userTookOverRef = useRef(false);
  const [paused, setPaused] = useState(false);
  // Only idle-tick while actually visible — no animating a strip nobody's looking at.
  const onScreen = useOnScreen(trackRef, { enabled: autoAdvance, threshold: 0.2 });
  // Vestibular safety: never auto-move for reduced-motion users.
  const reduced = usePrefersReducedMotion();

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setShowPrev(el.scrollLeft > 8);
    setShowNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, children]);

  const scrollByCard = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const first = el.firstElementChild;
    const w = (first?.getBoundingClientRect().width || itemWidth) + 14;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  }, [itemWidth]);

  // Real user gestures only. We can't use the `scroll` event to detect this —
  // our own auto-advance fires it, which would instantly stop the thing.
  useEffect(() => {
    if (!autoAdvance) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const takeOver = () => { userTookOverRef.current = true; setPaused(true); };
    const enter = () => setPaused(true);
    const leave = () => setPaused(false);
    ["pointerdown", "wheel", "touchstart", "keydown"].forEach(evt =>
      el.addEventListener(evt, takeOver, { passive: true }));
    el.addEventListener("mouseenter", enter);
    el.addEventListener("mouseleave", leave);
    el.addEventListener("focusin", enter);
    el.addEventListener("focusout", leave);
    return () => {
      ["pointerdown", "wheel", "touchstart", "keydown"].forEach(evt =>
        el.removeEventListener(evt, takeOver));
      el.removeEventListener("mouseenter", enter);
      el.removeEventListener("mouseleave", leave);
      el.removeEventListener("focusin", enter);
      el.removeEventListener("focusout", leave);
    };
  }, [autoAdvance]);

  useEffect(() => {
    if (!autoAdvance || paused || !onScreen || reduced || userTookOverRef.current) return undefined;

    const id = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 8;
      if (atEnd) el.scrollTo({ left: 0, behavior: "smooth" });  // gentle loop
      else scrollByCard(1);
    }, 4500);
    return () => clearInterval(id);
  }, [autoAdvance, paused, onScreen, reduced, scrollByCard, children]);

  const arrowStyle = (side) => ({
    position: "absolute", top: "50%", [side]: -4, transform: "translateY(-50%)",
    width: 34, height: 34, borderRadius: "50%", background: "#fff",
    border: `1px solid ${BORDER}`, boxShadow: "0 4px 14px rgba(15,16,36,0.14)",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", fontSize: 17, fontWeight: 800, color: INDIGO, zIndex: 2, lineHeight: 1,
  });

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={trackRef}
        style={{
          display: "flex", overflowX: "auto", scrollSnapType: "x mandatory",
          gap: 14, paddingBottom: 4, scrollbarWidth: "none", msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {Array.isArray(children) ? children.map((child, i) => (
          <div key={i} style={{ flex: `0 0 ${itemWidth}px`, width: itemWidth, scrollSnapAlign: "start" }}>
            {child}
          </div>
        )) : (
          <div style={{ flex: `0 0 ${itemWidth}px`, width: itemWidth, scrollSnapAlign: "start" }}>
            {children}
          </div>
        )}
      </div>
      {/* The arrows live outside the scroll track, so the track's gesture
          listener never sees them — mark takeover explicitly here. */}
      {showPrev && (
        <button type="button" onClick={() => { userTookOverRef.current = true; scrollByCard(-1); }}
          aria-label={prevLabel} style={arrowStyle("left")}>‹</button>
      )}
      {showNext && (
        <button type="button" onClick={() => { userTookOverRef.current = true; scrollByCard(1); }}
          aria-label={nextLabel} style={arrowStyle("right")}>›</button>
      )}
    </div>
  );
}

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "#fff", border: `1.5px solid ${open ? INDIGO + "33" : BORDER}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", textAlign: "left", padding: "16px 20px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700, color: NAVY, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        {q}
        <span style={{ fontSize: 18, color: INDIGO, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
      </button>
      {open && <div style={{ padding: "0 20px 16px", fontSize: 13, color: TEXT_MID, lineHeight: 1.7 }}>{a}</div>}
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle, mobile, color = INDIGO }) {
  return (
    <div style={{ textAlign: "center", marginBottom: mobile ? 30 : 40, maxWidth: 680, marginLeft: "auto", marginRight: "auto" }}>
      {eyebrow && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
          {eyebrow}
        </div>
      )}
      <h2 style={{ fontSize: mobile ? 25 : 33, fontWeight: 900, color: NAVY, margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.65, margin: 0 }}>{subtitle}</p>}
    </div>
  );
}

export default function HomeV2({ onNavigate, onOpenCase }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load admin-configurable plan data so the agency teaser price stays in sync
  // with /aba-agencies (both read getPlans() → platform_config). Until it loads
  // — and during prerender, where Supabase is blocked — getPlans() returns the
  // build-time defaults ($149 / 10 cases), same as the agency page.
  const [, setCfgLoaded] = useState(false);
  useEffect(() => { loadAllConfigs().then(() => setCfgLoaded(true)).catch(() => {}); }, []);
  const proPlan  = getPlans().find((p) => p.id === "pro") || {};
  const proMo    = proPlan.mo ?? 149;
  const proCases = proPlan.cases ?? 10;
  const proLogins = getAgencyPricing().teamLogins; // team logins Pro includes (platform_config via lib/agencyPricing)
  // Same derivation AgencyLanding uses for its starter-listing count — keeps
  // the "start with N free listings" claim in sync with platform_config.
  // Plans from Jan 2027: Starter's monthly price and included openings (lib/agencyPricing owns the numbers).
  const agencyPricing = getAgencyPricing(i18n.language);
  const starterSlotPrice = agencyPricing.starterMo;
  const isEsLocale = String(i18n.language || "").toLowerCase().startsWith("es");
  const promoDates = getStarterPromoDates();
  const promoEndDateShort = isEsLocale ? promoDates.endDateShortStrEs : promoDates.endDateShortStr;

  // Real, live data for the two homepage carousels. Both loaders are
  // guest-safe and never throw (they return [] on any error), so a failed or
  // empty load simply means the section renders nothing — no mock data, no
  // fabricated counts, ever presented as if it were real inventory.
  // Admin `public_case_browsing`: when OFF the Case Finder is a signup gate for
  // guests, so the primary CTA is "Get case alerts" instead of "Find Cases".
  const browsingOn = usePublicCaseBrowsing();
  const [openCases, setOpenCases] = useState([]);
  // False until the first load settles, so the hero can reserve the card's space
  // (skeleton) instead of popping in and shifting the layout.
  const [casesLoaded, setCasesLoaded] = useState(false);
  const [providerHighlights, setProviderHighlights] = useState([]);
  useEffect(() => {
    // Newest first, unless we can tell which US state the visitor is in (coarse
    // IP geolocation, see lib/visitorGeo.js): then that state's cases lead.
    // Cases and location load in parallel and the list is ordered ONCE when both
    // settle, so the hero card never swaps after it appears. getVisitorState
    // never rejects and gives up after ~800 ms, so it adds no wait when it is
    // slower than the cases query, and at most that cap otherwise (first visit
    // of a session only; the answer is cached in sessionStorage).
    Promise.all([
      loadPublicOpenCases(12).catch(() => []),
      getVisitorState().catch(() => null),
    ])
      .then(([rows, visitorState]) => setOpenCases(sortCasesForVisitor(rows, visitorState)))
      .catch(() => {})
      .finally(() => setCasesLoaded(true));
    loadPublicProviderHighlights(12).then(setProviderHighlights).catch(() => {});
  }, []);

  // The hero shows the first case; the carousel starts at the second so the
  // same case is never repeated on the page.
  const heroCase = openCases[0] || null;
  const carouselCases = openCases.slice(1);
  const showHeroCase = !casesLoaded || !!heroCase;   // skeleton while loading
  // With no case to show there is no right column, so the copy centers.
  const heroStacked = isMobile || !showHeroCase;

  // One click handler for the hero card and every carousel card: open that case's public page (/share/<id>, the same
  // page share links use), whatever the public browsing setting, so a visitor sees the case before being asked to
  // sign up (owner 2026-10-23). Its "Sign up free to apply" remembers the case through signup and onboarding
  // (lib/signupIntent pending case), and App reopens it with the form in view.
  const openCase = (c, placement) => {
    track(
      placement === "hero" ? A.HOMEPAGE_HERO_CASE_CLICKED : A.HOMEPAGE_CAROUSEL_CASE_APPLY_CLICKED,
      {
        supabaseMeta: { placement, browsing: browsingOn, case_id: c.id, case_type: c.type, state: c.state },
        ga4Params: { placement, browsing: browsingOn ? "on" : "off" },
      },
    );
    if (onOpenCase) onOpenCase(c.id);
    else window.location.assign(publicCasePath(c.id));
  };

  // Live homepage — indexable, self-canonical (the Spanish twin /es points at
  // itself; everything else canonicalizes to /).
  const isEsPath = typeof window !== "undefined" && window.location.pathname.replace(/\/+$/, "") === "/es";
  usePageSeo({
    title: t("homeV2.metaTitle"),
    description: t("homeV2.metaDescription"),
    canonical: isEsPath ? "/es" : "/",
    lang: (i18n.language || "en").startsWith("es") ? "es" : "en",
  });

  useFaqJsonLd("faq-jsonld-homev2", FAQ_KEYS.map((k) => ({
    q: t(`therapistLanding.${k}Q`),
    a: t(`therapistLanding.${k}A`),
  })));
  useSiteSchema(); // Organization + WebSite

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA navigation). Used by the discovery hub's card links — NOT for
  // app-route CTAs, which stay <button> so the prerender crawler doesn't
  // wander into the map app.
  const DiscoveryLink = ({ to, children }) => (
    <a
      href={seoHref(to)}
      onClick={(e) => { e.preventDefault(); onNavigate(to); }}
      style={{ fontSize: 13, fontWeight: 500, color: TEXT_MID, textDecoration: "none", transition: "color 0.15s" }}
      onMouseEnter={(e) => { e.currentTarget.style.color = INDIGO; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MID; }}
    >
      {children}
    </a>
  );

  // CTA handlers (named conversion events; analytics sinks land in Slice 2A/2B).
  const goFindCases = () => { track(A.HOMEPAGE_FIND_CASES_CLICKED); onNavigate("cases-v2"); };
  const goGetAlerts = () => { track(A.CASE_ALERT_CLICK, { supabaseMeta: { page: "/" } }); setSignupIntent("alerts"); onNavigate("auth"); };
  // Showcase button: straight to signup carrying the tracker intent (the hero's
  // quieter "Open Free Tracker" goes to the tracker landing page instead).
  const goTrackerSignup = () => { track(A.HOMEPAGE_TRACKER_CLICKED); track(A.TRACKER_SIGNUP_STARTED, { supabaseMeta: { source: "home_v2_showcase" } }); setSignupIntent("tracker"); onNavigate("auth"); };
  const goTracker   = () => { track(A.HOMEPAGE_TRACKER_CLICKED); track(A.TRACKER_SIGNUP_STARTED, { supabaseMeta: { source: "home_v2" } }); setSignupIntent("tracker"); onNavigate(TRACKER_VIEW); };
  // The homepage is the hub: each section links to its deeper standalone page
  // rather than repeating its full content.
  const goAgency        = () => { onNavigate("aba-agencies"); };
  // Primary agency CTA: the focused conversion page (same destination as the outreach emails).
  const goPostCase      = () => { onNavigate("post-a-case"); };
  const goTrackerPage    = () => { onNavigate("free-aba-timesheet-payment-tracker"); };

  const sectionPad = isMobile ? "48px 18px" : "72px 48px";
  const maxW = 1080;

  // Reusable CTA buttons (the two co-primary therapist actions).
  const PrimaryBtn = ({ onClick, children, full }) => (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      width: full ? "100%" : "auto", padding: isMobile ? "14px 26px" : "15px 32px",
      background: T.gradientIndigo, color: "#fff",
      border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer",
      boxShadow: T.shadowIndigo, fontFamily: T.fontBody,
    }}>{children}</button>
  );

  const heroCaseNode = showHeroCase ? (
    <div data-testid="home-hero-case" aria-busy={!casesLoaded}>
      {heroCase
        ? <HomeCaseCard c={heroCase} onOpen={openCase} placement="hero" />
        : <HomeCaseCardSkeleton />}
    </div>
  ) : null;

  return (
    <div style={{ fontFamily: T.fontBody, background: "#fff", minHeight: "100vh" }}>

      {/* The shared TopNav (rendered by App) provides the logo, language
          switcher, and nav — same as every other public page. */}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "36px 18px 28px" : "72px 48px 80px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, left: "15%", width: 460, height: 460, borderRadius: "50%", background: "rgba(79,70,229,0.18)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: 1080, margin: "0 auto", display: "grid", gridTemplateColumns: heroStacked ? "1fr" : "1.1fr 0.9fr", gap: isMobile ? 32 : 48, alignItems: "center", ...(heroStacked && !isMobile ? { maxWidth: 760 } : {}) }}>
          {/* Left: copy + CTAs */}
          <div style={{ textAlign: heroStacked ? "center" : "left" }}>
            <h1 style={{ fontSize: isMobile ? 30 : 46, fontWeight: 900, color: "#fff", margin: "0 0 14px", lineHeight: 1.12, letterSpacing: "-0.025em" }}>
              {t("homeV2.heroTitle1")}
            </h1>
            {/* NPI trust line: a subhead, not part of the headline. */}
            <p data-testid="home-hero-trust" style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              justifyContent: heroStacked ? "center" : "flex-start", textAlign: heroStacked ? "left" : "left",
              fontSize: isMobile ? 14.5 : 16, fontWeight: 700, color: INDIGO_LT, lineHeight: 1.45,
              maxWidth: 560, margin: heroStacked ? "0 auto 14px" : "0 0 14px",
            }}>
              <ShieldIcon size={isMobile ? 17 : 19} color={INDIGO_LT} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{t("homeV2.heroTrust")}</span>
            </p>
            <p style={{ fontSize: isMobile ? 15 : 17, color: "#cbd5e1", lineHeight: 1.6, maxWidth: 560, margin: heroStacked ? "0 auto 22px" : "0 0 28px" }}>
              {t("homeV2.heroSubhead")}
            </p>
            {/* One unambiguous primary action. The tracker is a separate,
                deliberately quieter path below — it serves people already
                working a case, which is a different visitor than the one this
                page is trying to convert. */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: heroStacked ? "center" : "flex-start" }}>
              {browsingOn
                ? <PrimaryBtn onClick={goFindCases}><MapPinIcon size={16} color="#fff" /> {t("homeV2.ctaFindCases")}</PrimaryBtn>
                : <PrimaryBtn onClick={goGetAlerts}><BellIcon size={16} color="#fff" /> {t("homeV2.ctaGetAlerts")}</PrimaryBtn>}
            </div>
            {isMobile && heroCaseNode && <div style={{ marginTop: 26, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>{heroCaseNode}</div>}
            <div style={{ marginTop: 22 }}>
              <p style={{ fontSize: 12.5, color: TEXT_LIGHT, margin: "0 0 8px", maxWidth: 420, marginLeft: heroStacked ? "auto" : 0, marginRight: heroStacked ? "auto" : 0 }}>
                {t("homeV2.heroTrackerLead")}
              </p>
              <button onClick={goTracker} style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "9px 16px", background: "transparent", color: "#cbd5e1",
                border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10,
                fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody,
              }}>
                <DollarIcon size={14} color="#cbd5e1" /> {t("homeV2.ctaTracker")}
              </button>
            </div>
            <p style={{ marginTop: 18, fontSize: 12.5, color: TEXT_LIGHT, maxWidth: 520, marginLeft: heroStacked ? "auto" : 0, marginRight: heroStacked ? "auto" : 0 }}>
              {t("homeV2.heroSupport")}
            </p>
            <p style={{ marginTop: 12, fontSize: 12.5, color: TEXT_LIGHT }}>
              {t("homeV2.haveAccount")}{" "}
              <button onClick={() => onNavigate("auth")} style={{ background: "none", border: "none", color: INDIGO_LT, fontWeight: 700, cursor: "pointer", fontSize: 12.5, padding: 0 }}>
                {t("homeV2.signIn")}
              </button>
            </p>
          </div>
          {/* Right (desktop): ONE real open case ("Just posted"). A same-size
              skeleton holds its place while cases load; with no open cases the
              column is gone and the copy centers. Never a sample or invented
              case. On mobile the same card sits under the primary CTA instead. */}
          {!isMobile && heroCaseNode}
        </div>
      </header>

      {/* ── Case Finder preview ──────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <SectionHeading eyebrow={t("homeV2.cfEyebrow")} title={t("homeV2.cfTitle")} subtitle={t("homeV2.cfSubtitle")} mobile={isMobile} />
          {/* Real open cases after the hero's one (so none repeats). Renders
              nothing, heading included, when no other case is open. */}
          {carouselCases.length > 0 && (
            <div data-testid="home-case-carousel" style={{ marginBottom: 26 }}>
              <p style={{ textAlign: "center", fontSize: 14.5, fontWeight: 600, color: NAVY, margin: "0 0 16px" }}>{t("homeV2.cfOpenNow")}</p>
              <CardCarousel prevLabel={t("homeV2.carouselPrev")} nextLabel={t("homeV2.carouselNext")} itemWidth={300}>
                {carouselCases.map((c) => (
                  <HomeCaseCard key={c.id} c={c} onOpen={openCase} placement="carousel" compact />
                ))}
              </CardCarousel>
            </div>
          )}
          {/* Compare-on chips */}
          <ul data-testid="home-compare-labels" style={{ listStyle: "none", padding: 0, display: "flex", flexWrap: "wrap", gap: "10px 22px", justifyContent: "center", margin: "0 0 22px" }}>
            {[
              { Icon: MapPinIcon,     k: "cfCompareLocation" },
              { Icon: DollarIcon,     k: "cfComparePay" },
              { Icon: CalendarIcon,   k: "cfCompareSchedule" },
              { Icon: ShieldIcon,     k: "cfCompareCredential" },
              { Icon: BriefcaseIcon,  k: "cfCompareSetting" },
              { Icon: ClockIcon,      k: "cfCompareHours" },
            ].map(({ Icon, k }) => (
              <li key={k} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: TEXT_MID, cursor: "default" }}>
                <Icon size={14} color={INDIGO} /> <span>{t(`homeV2.${k}`)}</span>
              </li>
            ))}
          </ul>
          {/* No CTA here: the hero owns the page's one call to action. */}
          <p style={{ textAlign: "center", fontSize: 12.5, color: TEXT_LIGHT, margin: 0, fontStyle: "italic" }}>{t("homeV2.cfPreviewNote")}</p>
        </div>
      </section>

      {/* ── Providers on ABAcases — real, non-identifying highlights. Renders
              nothing at all (no heading, no empty shell) when there are none. ── */}
      {providerHighlights.length > 0 && (
        <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <SectionHeading eyebrow={t("homeV2.provEyebrow")} title={t("homeV2.provTitle")} subtitle={t("homeV2.provSubtitle")} mobile={isMobile} />
            <CardCarousel prevLabel={t("homeV2.carouselPrev")} nextLabel={t("homeV2.carouselNext")} itemWidth={220} autoAdvance>
              {providerHighlights.map((p, i) => (
                <ProviderCard key={i} p={p} />
              ))}
            </CardCarousel>
          </div>
        </section>
      )}

      {/* ── Free ABA Timesheet & Payment Tracker showcase. Sits after the case
              carousel and the provider highlights, so the page leads with real
              open cases (owner decision, 2026-10-16). ── */}
      <section data-testid="home-tracker-showcase" style={{ background: `linear-gradient(180deg, ${NAVY_MID} 0%, ${NAVY} 100%)`, padding: isMobile ? "32px 16px 52px" : "76px 48px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 38, maxWidth: 700, marginLeft: "auto", marginRight: "auto" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: INDIGO_LT, fontFamily: T.fontMono }}>
              {t("homeV2.trkEyebrow")}
            </div>
            <h2 style={{ fontSize: isMobile ? 27 : 36, fontWeight: 900, color: "#fff", margin: "0 0 14px", letterSpacing: "-0.025em", lineHeight: 1.12 }}>{t("homeV2.trkTitle")}</h2>
            <p style={{ fontSize: 15.5, color: "rgba(255,255,255,0.66)", lineHeight: 1.65, margin: 0 }}>{t("homeV2.trkSubtitle")}</p>
          </div>

          <TrackerShowcase isMobile={isMobile} accent={INDIGO} mobileHideTabs={["calendar"]} />

          {/* Feature bullets */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12, maxWidth: 820, margin: "34px auto 0" }}>
            {["trkBullet1", "trkBullet2", "trkBullet3", "trkBullet4", "trkBullet5", "trkBullet6", "trkBullet7", "trkBullet8"].map((k) => (
              <div key={k} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 14px" }}>
                <CheckIcon size={15} color="#34d399" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>{t(`homeV2.${k}`)}</span>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 32 }}>
            <div style={{ marginBottom: 16 }}>
              <PrimaryBtn onClick={goTrackerSignup}><DollarIcon size={16} color="#fff" /> {t("homeV2.trkCta")}</PrimaryBtn>
            </div>
            <a
              href="/free-aba-timesheet-payment-tracker/"
              onClick={(e) => { e.preventDefault(); goTrackerPage(); }}
              style={{ fontSize: 14, fontWeight: 700, color: INDIGO_LT, textDecoration: "none" }}
            >
              {t("homeV2.trkCtaExplore")} →
            </a>
            <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", marginTop: 14, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>{t("homeV2.trkSupport")}</p>
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <SectionHeading title={t("homeV2.hiwTitle")} mobile={isMobile} />
          {/* Product walkthrough: real app screens with sample data. Loads only when near the viewport, plays muted on a
              loop, pauses off screen; reduced-motion visitors get the poster and controls. Phones get the vertical cut
              (540x960, about 2 MB) so the on-screen text stays readable; wider screens get 720p (about 3 MB WebM).
              The key remounts it when the width crosses the breakpoint, so the right file loads. */}
          <LazyVideo
            key={HIW_VIDEO[isMobile ? "phone" : "wide"].poster}
            testId="home-hiw-video"
            {...HIW_VIDEO[isMobile ? "phone" : "wide"]}
            label={t("homeV2.hiwVideoLabel")}
            style={isMobile
              ? { maxWidth: 320, margin: "0 auto 22px", borderRadius: 14, boxShadow: "0 18px 50px rgba(15,16,36,0.18)" }
              : { maxWidth: 1000, margin: "0 auto 30px", borderRadius: 18, boxShadow: "0 18px 50px rgba(15,16,36,0.18)" }}
          />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 18 }}>
            {[
              { n: "1", Icon: UsersIcon,      tk: "step1Title", bk: "step1Body" },
              { n: "2", Icon: MapPinIcon,     tk: "step2Title", bk: "step2Body" },
              { n: "3", Icon: ClockIcon,      tk: "step3Title", bk: "step3Body" },
            ].map(({ n, Icon, tk, bk }) => (
              <div key={n} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "26px 24px", position: "relative", boxShadow: "0 2px 14px rgba(15,16,36,0.05)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={20} color={INDIGO} />
                  </div>
                  <span style={{ fontSize: 38, fontWeight: 900, color: "#ede9fe", fontFamily: T.fontMono, lineHeight: 1 }}>{n}</span>
                </div>
                <div style={{ fontSize: 16.5, fontWeight: 800, color: NAVY, marginBottom: 7 }}>{t(`homeV2.${tk}`)}</div>
                <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(`homeV2.${bk}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ONE pitch section. Replaces three overlapping ones ("Sound familiar?",
              "Not a job board", "Why choose …"): pain points, the "fixes both
              sides" line, and the four reasons, with no duplicated points. ── */}
      <section data-testid="home-pitch" style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <SectionHeading title={t("homeV2.trustTitle")} subtitle={t("homeV2.whySubtitle")} mobile={isMobile} />
          <div style={{ maxWidth: 760, margin: `0 auto ${isMobile ? 28 : 36}px` }}>
            <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: TEXT_MID, textAlign: "center", margin: "0 0 14px" }}>{t("homeV2.painTitle")}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "10px 24px" }}>
              {["pain1", "pain2", "pain4", "pain5"].map((k) => (
                <li key={k} style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13.5, color: TEXT_MID, lineHeight: 1.5 }}>
                  <XIcon size={14} color={T.red} style={{ flexShrink: 0, marginTop: 3 }} /> <span>{t(`homeV2.${k}`)}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 15, color: NAVY, fontWeight: 700, textAlign: "center", lineHeight: 1.6, margin: 0 }}>{t("homeV2.painResolve")}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {[
              { Icon: DollarIcon,  tk: "trust1", bk: "trust1d" },
              { Icon: ShieldIcon,  tk: "trust2", bk: "trust2d" },
              { Icon: CheckCircleIcon, tk: "trust3", bk: "trust3d" },
              { Icon: ZapIcon,     tk: "trust4", bk: "trust4d" },
            ].map(({ Icon, tk, bk }) => (
              <div key={tk} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{t(`homeV2.${tk}`)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(`homeV2.${bk}`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section data-testid="home-faq" style={{ background: "#fff", padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <SectionHeading title={t("homeV2.faqTitle")} mobile={isMobile} />
          {FAQ_KEYS.map((k, i) => (
            <FaqItem key={k} q={t(`therapistLanding.${k}Q`)} a={t(`therapistLanding.${k}A`)} defaultOpen={i === 0} />
          ))}
        </div>
      </section>

      {/* ── Agency card. Deliberately AFTER the FAQ so the provider flow
              (hero, tracker, cases, why, FAQ) is uninterrupted. ── */}
      <section data-testid="home-agency" style={{ padding: isMobile ? "0 18px 48px" : "0 48px 72px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, borderRadius: 20, padding: isMobile ? "32px 22px" : "40px 44px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 20, alignItems: "center" }}>
            <div style={{ textAlign: isMobile ? "center" : "left" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: INDIGO_LT }}>
                <BriefcaseIcon size={13} color={INDIGO_LT} /> {t("homeV2.agencyEyebrow")}
              </div>
              <h2 style={{ fontSize: isMobile ? 22 : 27, fontWeight: 900, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.02em" }}>{t("homeV2.agencyTitle")}</h2>
              <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.8)", lineHeight: 1.6, margin: 0, maxWidth: 520 }}>{promoDates.isPromoActive
                ? t("homeV2.agencySubtitle", { price: proMo, cases: proCases, logins: proLogins, starterPrice: starterSlotPrice, starterIncluded: agencyPricing.starterIncluded, count: agencyPricing.promoCount, endDateShort: promoEndDateShort, startDate: agencyPricing.startDate })
                : t("homeV2.agencySubtitlePostPromo", { price: proMo, cases: proCases, logins: proLogins, starterPrice: starterSlotPrice, starterIncluded: agencyPricing.starterIncluded })}
                {/* Workforce roster limit per plan: named only once Workforce is released (workforce_enabled). */}
                {agencyPricing.workforceIncluded && ` ${t("agencyPricing.workforceLine", { starter: agencyPricing.workforceMaxStarter, pro: agencyPricing.workforceMaxPro })}`}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: isMobile ? "stretch" : "flex-end" }}>
              <button onClick={goPostCase} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 28px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", boxShadow: T.shadowIndigo, fontFamily: T.fontBody }}>
                {promoDates.isPromoActive ? t("agencyPricing.cta") : t("agencyPricing.ctaPaid")} →
              </button>
              <button onClick={goAgency} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 24px", background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.45)", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                {t("homeV2.agencyCta")} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Discovery hub — grouped card grid of crawlable internal links so
              Googlebot (and the prerender crawler) can discover every SEO
              landing, while reading as a polished human-facing directory
              rather than a loose list of SEO links. ───────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: isMobile ? "40px 18px" : "56px 48px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: NAVY, margin: "0 0 8px", letterSpacing: "-0.01em" }}>{t("homeV2.discoveryHubTitle")}</h2>
          <p style={{ fontSize: 14, color: TEXT_MID, margin: "0 0 24px", maxWidth: 620 }}>{t("homeV2.discoveryHubSubtitle")}</p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {[...DISCOVERY_GROUPS.slice(0, 3), moreStatesGroup(t, i18n.language?.startsWith("es")), ...DISCOVERY_GROUPS.slice(3)].map((group) => (
              <div key={group.key} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 18px" }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                  {t(group.titleKey)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {group.links.map((l) => (
                    <DiscoveryLink key={l.to} to={l.to}>{l.label || t(l.labelKey)}</DiscoveryLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
